'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { EERIE, GYPSUM } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { API_CALLS_DAILY } from 'pricing-pages-lib/constants/LimitIds';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { Fragment, useContext } from 'react';
import { useSelector } from 'react-redux';
import { RECURRING } from 'self-service-api/constants/ChargeTypes';
import { API_LIMIT_INCREASE } from 'self-service-api/constants/ProductNames';
import { PRODUCT_VERSION, QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import { CONTACT, MARKETING_CONTACT_DEPRECATED, USER } from 'self-service-api/constants/UnitTypes';
import styled from 'styled-components';
import UICloseButton from 'UIComponents/button/UICloseButton';
import { purchasedContactsQuantitySelector } from '../../selectors/ContactsSelectors';
import { activeCurrencySelector } from '../../selectors/CurrencySelectors';
import { calculatedPriceForProductConfigurationsInListSelector, planAdditionalCostsForProductConfigurationInListSelector } from '../../selectors/PricingSelectors';
import { productsFromProductInventoryQuantitySelector } from '../../selectors/ProductInventorySelectors';
import { billingNameKeySelector, maxProductVersionQuantitySelector, productForSkuIdSelector, skuIdIsHubSelector, skuSelector } from '../../selectors/ProductsSelectors';
import PriceDisplay from '../common/pricing/PriceDisplay';
import { PlanBuilderContext } from './PlanBuilder';
const ShoppingCartItemContainer = styled.div.withConfig({
  displayName: "ShoppingCartItem__ShoppingCartItemContainer",
  componentId: "sc-1o2stu9-0"
})(["background-color:", ";padding:16px 24px;position:relative;&:not(:last-child){margin-bottom:8px;}"], GYPSUM);
const PlanTitle = styled.div.withConfig({
  displayName: "ShoppingCartItem__PlanTitle",
  componentId: "sc-1o2stu9-1"
})(["font-weight:600;"]);
const CloseButton = styled(UICloseButton).withConfig({
  displayName: "ShoppingCartItem__CloseButton",
  componentId: "sc-1o2stu9-2"
})(["position:absolute;top:10px;right:10px;"]);

const ShoppingCartUnit = ({
  skuId,
  quantity,
  inline = true
}) => {
  const sku = useSelector(state => skuSelector(state, {
    skuId
  }));
  let billingNameKey = useSelector(state => billingNameKeySelector(state, {
    skuId
  }));
  let message;

  if (sku.skuType === QUANTITY_PACK) {
    const {
      quantityUnit: {
        unitType
      }
    } = sku;
    message = /*#__PURE__*/_jsx(FormattedMessage, {
      message: unitType === MARKETING_CONTACT_DEPRECATED || unitType === CONTACT ? 'pricingPage.planBuilder.shoppingCart.additionalContacts' : quantity === 1 ? 'pricingPage.planBuilder.shoppingCart.additionalUsers.one' : 'pricingPage.planBuilder.shoppingCart.additionalUsers.other',
      options: {
        count: quantity
      }
    });
  } else {
    if (billingNameKey === 'brand-domain') {
      billingNameKey = 'brand-domain-new';
    }

    message = /*#__PURE__*/_jsx(FormattedMessage, {
      message: `pricingPage.planBuilder.shoppingCart.addonsAndServices.${billingNameKey}`,
      options: {
        count: quantity
      }
    });
  }

  return inline ? message : /*#__PURE__*/_jsx("div", {
    children: message
  });
};

function ShoppingCartItem({
  skuId,
  allProductConfigurations = [],
  onRemove,
  isCloseable = true,
  shouldDisplayPrice = true,
  queryParamTerm,
  priceOverride
}) {
  const {
    screen
  } = useContext(PlanBuilderContext);
  const productConfiguration = allProductConfigurations.find(({
    skuId: searchSkuId
  }) => skuId === searchSkuId);
  const skuIdIsHub = useSelector(state => skuIdIsHubSelector(state, {
    skuId
  }));
  const unitsProductConfigurations = useSelector(state => {
    const sku = skuSelector(state, {
      skuId
    });
    const {
      quantityPackDetails,
      skuType,
      quantifiable
    } = sku; // If the whole line item is just for one quantity pack or addon

    if (!skuIdIsHub) {
      if (skuType === PRODUCT_VERSION) {
        const maxMerchQuantity = maxProductVersionQuantitySelector(state, {
          skuId
        });
        const isQuantifiable = quantifiable && maxMerchQuantity > 1;
        let unitQuantityToUse;
        unitQuantityToUse = isQuantifiable ? productConfiguration.quantity - productsFromProductInventoryQuantitySelector(state, {
          skuId
        }) : null;
        const {
          apiName
        } = productForSkuIdSelector(state, {
          skuId
        });

        if (apiName === API_LIMIT_INCREASE) {
          // Todo need backend to supply ProductVersion limits
          const limits = skuSelector(state, {
            skuId
          }).allProductVersionLimits;
          const dailyLimit = limits[API_CALLS_DAILY];
          if (!dailyLimit) return [];
          unitQuantityToUse = dailyLimit;
        }

        if (unitQuantityToUse === null) return [];
        return [createProductConfig({
          skuId,
          quantity: unitQuantityToUse
        })];
      } else if (skuType === QUANTITY_PACK) {
        const {
          quantityUnit: {
            unitType
          }
        } = sku;
        let quantityAdjustedForOwnership = productConfiguration.quantity;

        if (unitType === MARKETING_CONTACT_DEPRECATED || unitType === CONTACT) {
          quantityAdjustedForOwnership -= purchasedContactsQuantitySelector(state, {
            skuIdForMCCheck: skuId
          });
        } else if (unitType === USER) {
          quantityAdjustedForOwnership -= productsFromProductInventoryQuantitySelector(state, {
            skuId
          });
        }

        return [createProductConfig({
          skuId: productConfiguration.skuId,
          quantity: quantityAdjustedForOwnership
        })];
      }

      return [productConfiguration];
    }

    return quantityPackDetails.map(({
      skuId: quantityPackSkuId,
      quantityUnit: {
        includedWithProductVersion
      }
    }) => {
      let quantityToUse = includedWithProductVersion;
      const cartConfig = allProductConfigurations.find(({
        skuId: searchSkuId
      }) => searchSkuId === quantityPackSkuId);
      if (cartConfig) quantityToUse += cartConfig.quantity;
      return createProductConfig({
        skuId: quantityPackSkuId,
        quantity: quantityToUse
      });
    });
  });
  const unitsProductConfigurationsForPriceDisplay = useSelector(state => allProductConfigurations.filter(({
    skuId: searchSkuId
  }) => {
    const {
      skuType,
      productVersionSkuId
    } = skuSelector(state, {
      skuId: searchSkuId
    });
    return skuType === QUANTITY_PACK && productVersionSkuId === skuId;
  }));
  const activeCurrencyCode = useSelector(activeCurrencySelector);
  const priceCalculationProductConfigurations = [productConfiguration, ...unitsProductConfigurationsForPriceDisplay];
  const priceObject = useSelector(state => priceOverride || (skuIdIsHub ? calculatedPriceForProductConfigurationsInListSelector(state, {
    allProductConfigurations,
    productConfigurations: priceCalculationProductConfigurations,
    queryParamTerm
  }) : planAdditionalCostsForProductConfigurationInListSelector(state, {
    allProductConfigurations,
    productConfigurations: priceCalculationProductConfigurations,
    queryParamTerm
  })));
  const isRecurring = useSelector(state => productForSkuIdSelector(state, {
    skuId
  }).chargeType === RECURRING);
  const price = isRecurring ? priceObject.monthly.calculatedPrice : priceObject.oneTimeFee.calculatedPrice;
  const billingNameKey = useSelector(state => billingNameKeySelector(state, {
    skuId
  }));
  const {
    apiName
  } = useSelector(state => productForSkuIdSelector(state, {
    skuId
  }));

  const handleRemove = () => {
    getUsageTracker().track('shoppingCartInteraction', {
      action: 'removed item',
      skuId,
      productName: apiName,
      screen
    });
    onRemove();
  };

  return /*#__PURE__*/_jsxs(ShoppingCartItemContainer, {
    "data-selenium-class": "shopping-cart-item-container",
    children: [/*#__PURE__*/_jsx(PlanTitle, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        "data-selenium-class": "shopping-cart-item-title",
        message: `pricing-pages.products.${billingNameKey}.title`
      })
    }), /*#__PURE__*/_jsxs("div", {
      children: [shouldDisplayPrice && /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(PriceDisplay, {
          price: price,
          currencyCode: activeCurrencyCode
        }), isRecurring && /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.planBuilder.shoppingCart.perMonth",
          useGap: true
        }), unitsProductConfigurations.length === 1 && ' | ']
      }), unitsProductConfigurations.map(({
        skuId: quantityPackSkuId,
        quantity
      }) => /*#__PURE__*/_jsx(ShoppingCartUnit, {
        skuId: quantityPackSkuId,
        quantity: quantity,
        inline: unitsProductConfigurations.length === 1
      }, quantityPackSkuId))]
    }), isCloseable && /*#__PURE__*/_jsx(CloseButton, {
      color: EERIE,
      onClick: handleRemove,
      size: "medium",
      "data-selenium-class": "shopping-cart-item-remove"
    })]
  });
}

export default ShoppingCartItem;